import type {
  SeminarConfigModel,
  SeminarCRUDModel,
  SeminarListModel,
  SeminarReportListModel,
  SeminarStudentModel,
} from 'models/seminar_model';
import http from './http';
import type { Dayjs } from 'plugins/dayjs';
import type { FiltersSeminars } from 'store/slices/seminarioSlice';

export const getSeminarsService = async (filtersParams: FiltersSeminars) => {
  try {
    const filters = [];
    if (filtersParams?.name) {
      filters.push(`nombre=${filtersParams.name}`);
    }
    if (filtersParams?.gestion) {
      filters.push(`gestion=${filtersParams.gestion}`);
    }

    if (filtersParams?._limit) {
      filters.push(`_limit=${filtersParams._limit}`);
    }
    if (filtersParams?._page) {
      filters.push(`_page=${filtersParams._page}`);
    }

    return await http.get<{ rows: SeminarListModel[]; count: number }>(
      `/seminarios${filters.length ? '?' + filters.join('&') : ''}`,
    );
  } catch (error) {
    console.error('[getSeminarsService]  Error -> ', error);
    return {
      rows: [],
      count: 0,
    };
  }
};

export const getSeminarsReportService = async (
  filtersParams: FiltersSeminars,
) => {
  try {
    const filters = [];
    if (filtersParams?.name) {
      filters.push(`nombre=${filtersParams.name}`);
    }
    if (filtersParams?.studentId) {
      filters.push(`idEstudiante=${filtersParams.studentId}`);
    }
    if (filtersParams?.desde) {
      filters.push(`fechaDesde=${filtersParams.desde}`);
    }
    if (filtersParams?.hasta) {
      filters.push(`fechaHasta=${filtersParams.hasta}`);
    }

    if (filtersParams?._limit) {
      filters.push(`_limit=${filtersParams._limit}`);
    }
    if (filtersParams?._page) {
      filters.push(`_page=${filtersParams._page}`);
    }

    return await http.get<{ rows: SeminarReportListModel[]; count: number }>(
      `/seminarios/reports${filters.length ? '?' + filters.join('&') : ''}`,
    );
  } catch (error) {
    console.error('[getSeminarsReportService]  Error -> ', error);
    return {
      rows: [],
      count: 0,
    };
  }
};

export const createSeminarService = async (data: SeminarCRUDModel) => {
  try {
    return await http.post<{ id: string } | null, SeminarCRUDModel>(`/seminarios`, data);
  } catch (error) {
    // El toast de error ya lo muestra http.ts a partir de error.response.data.error
    console.log('Error en createSeminarService', error);
    return null;
  }
};

export const editSeminarService = async (
  id: string,
  data: SeminarCRUDModel,
) => {
  try {
    return await http.put<{ id: string } | null, SeminarCRUDModel>(`/seminarios/${id}`, data);
  } catch (error) {
    console.log('Error en editSeminarService', error);
    return null;
  }
};

export const editScheduleSeminarService = async (
  id: string,
  data: {
    fechaCambio: string | Dayjs;
    horarios: Array<{ dia: string; horaInicio: string; horaFin: string }>;
  },
) => {
  try {
    return await http.put<{ id: string } | null, typeof data>(`/seminarios/${id}/horarios`, data);
  } catch (error) {
    console.log('Error en editScheduleSeminar', error);
    return null;
  }
};

export const deleteSeminarService = async (id: string) => {
  try {
    return await http.delete<{ id: string } | null>(`/seminarios/${id}`);
  } catch (error) {
    console.log('Error en deleteSeminarService', error);
    return null;
  }
};

// #region CONFIG SEMINAR
export const loadConfigSeminarService = async (year: string | number) => {
  try {
    return await http.get<SeminarConfigModel | null>(
      `/seminarios/configuracion?gestion=${year}`,
    );
  } catch (error) {
    console.error('[loadConfigSeminarService]  Error -> ', error);
    return null;
  }
};

export const createSeminarConfigService = async (data: SeminarConfigModel) => {
  try {
    return await http.post<SeminarConfigModel | null, SeminarConfigModel>(`/seminarios/configuracion`, data);
  } catch (error) {
    console.log('Error en createSeminarConfigService', error);
    return null;
  }
};

export const getSeminarStudentsService = async (seminarId: string) => {
  try {
    return await http.get<{ estudianteId: string; nombre: string; curso: string }[]>(`/seminarios/${seminarId}/estudiantes`);
  } catch (error) {
    console.error('[getSeminarStudentsService] Error -> ', error);
    return [];
  }
};

export const getSeminariosByEstudianteService = async (
  estudianteId: string,
) => {
  try {
    return await http.get<{ rows: SeminarStudentModel[]; count: number }>(`/seminarios/estudiante/${estudianteId}`);
  } catch (error) {
    console.error('[getSeminariosByEstudianteService] Error -> ', error);
    return { rows: [], count: 0 };
  }
};

export const getSeminariosHistorialByEstudianteService = async (
  estudianteId: string,
  page = 1,
  limit = 10,
) => {
  try {
    return await http.get<{ rows: SeminarStudentModel[]; count: number }>(
      `/seminarios/estudiante/${estudianteId}/historial?_page=${page}&_limit=${limit}`,
    );
  } catch (error) {
    console.error('[getSeminariosHistorialByEstudianteService] Error -> ', error);
    return { rows: [], count: 0 };
  }
};

export const editSeminarConfigService = async (data: SeminarConfigModel) => {
  try {
    return await http.put<SeminarConfigModel | null, SeminarConfigModel>(
      `/seminarios/${data.id}/configuracion`,
      data,
    );
  } catch (error) {
    console.log('Error en editSeminarConfigService', error);
    return null;
  }
};
